import { useEffect, useState } from 'react';

/**
 * Lecture à voix haute des étapes (US-60).
 *
 * ### Pourquoi elle est **éteinte par défaut**
 *
 * Une application qui se met à parler sans qu'on l'ait demandé est une
 * mauvaise surprise — dans une cuisine où quelqu'un d'autre dort à l'étage,
 * c'est une faute. La lecture existe donc sous deux formes, et l'ordre compte :
 *
 * 1. **à la demande** — la commande vocale « répète » relit l'étape en cours,
 *    quel que soit le réglage. C'est le geste qu'on fait les mains dans la
 *    pâte, quand on n'a pas vu l'écran passer à l'étape suivante ;
 * 2. **en continu** — un réglage, dans « Mes préférences », fait lire chaque
 *    étape à son arrivée. **Désactivé tant qu'on ne l'a pas allumé.**
 *
 * ### Dégradation
 *
 * `speechSynthesis` manque sur certains navigateurs et dans jsdom. Tout passe
 * par `parler()`, qui ne fait rien plutôt que de lever : une fonction de
 * confort ne doit jamais casser le mode cuisine.
 */

const CLE_REGLAGE = 'mlc:lecture-vocale';

type Ecouteur = () => void;
const ecouteurs = new Set<Ecouteur>();

export function isSpeechSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

export function lireReglageLecture(): boolean {
  try {
    return localStorage.getItem(CLE_REGLAGE) === 'oui';
  } catch {
    return false;
  }
}

export function ecrireReglageLecture(actif: boolean): void {
  try {
    localStorage.setItem(CLE_REGLAGE, actif ? 'oui' : 'non');
  } catch {
    // Réglage de confort : un stockage refusé ne doit rien casser.
  }
  ecouteurs.forEach((ecouteur) => { ecouteur(); });
}

/**
 * Réglage partagé par tous les composants montés — les réglages et le mode
 * cuisine peuvent être à l'écran en même temps sur grand écran, et un état
 * local dans chacun les ferait diverger.
 */
export function useLectureVocale(): { actif: boolean; setActif: (valeur: boolean) => void; possible: boolean } {
  const [actif, setEtat] = useState(() => lireReglageLecture());

  useEffect(() => {
    function synchroniser(): void { setEtat(lireReglageLecture()); }
    ecouteurs.add(synchroniser);
    // Un autre onglet a pu changer le réglage.
    window.addEventListener('storage', synchroniser);
    return () => {
      ecouteurs.delete(synchroniser);
      window.removeEventListener('storage', synchroniser);
    };
  }, []);

  return {
    actif,
    setActif: (valeur: boolean) => { ecrireReglageLecture(valeur); },
    possible: isSpeechSupported(),
  };
}

/**
 * Lit un texte. Interrompt toujours la lecture en cours : deux étapes
 * prononcées en même temps ne s'entendent ni l'une ni l'autre.
 */
export function parler(texte: string): void {
  if (!isSpeechSupported()) return;
  const propre = texte.trim();
  if (propre === '') return;
  try {
    window.speechSynthesis.cancel();
    const enonce = new SpeechSynthesisUtterance(propre);
    enonce.lang = 'fr-FR';
    // Un peu plus lent que la valeur par défaut : on écoute de l'autre bout
    // de la cuisine, avec une hotte qui tourne.
    enonce.rate = 0.95;
    window.speechSynthesis.speak(enonce);
  } catch {
    // Confort : on n'insiste pas.
  }
}

export function arreterLecture(): void {
  if (!isSpeechSupported()) return;
  try {
    window.speechSynthesis.cancel();
  } catch {
    // Idem.
  }
}
